import React, { useState } from 'react';
import {
    Box,
    Button,
    FlexBox,
    Icon,
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    Typography,
} from '@vp/swan';
import { HelpMeAnswerButton } from './HelpMeAnswerButton';
import type { TileChoiceOption } from '../types/selectors';

export type { TileChoiceOption };

export interface TileChoiceSelectorProps {
    title: string;
    options: TileChoiceOption[];
    defaultValue?: string;
    onComplete: (option: TileChoiceOption) => void;
}

// Map tile-choice icon keys to Swan icon types
const iconTypeMap = {
    business: 'business',
    personal: 'personal',
    online: 'workWithAPro',
    video: 'videoChat',
} as const;

export function TileChoiceSelector({
    title,
    options,
    defaultValue,
    onComplete,
}: TileChoiceSelectorProps) {
    const [selectedValue, setSelectedValue] = useState<string | null>(defaultValue ?? null);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
    };

    const handleContinue = () => {
        const option = options.find((o) => o.value === selectedValue);
        if (option) onComplete(option);
    };

    return (
        <Box
            style={{
                backgroundColor: 'var(--swan-sem-color-bg-active)',
                border: '1px solid var(--swan-sem-color-border-standard)',
                padding: 'var(--swan-sem-space-5)',
                borderRadius:
                    'var(--swan-sem-border-radius-strong) var(--swan-sem-border-radius-strong) 0 0',
            }}
        >
            {/* Header */}
            <Box
                style={{
                    minHeight: '44px',
                    marginBottom: 'var(--swan-sem-space-5)',
                }}
            >
                <Typography fontSkin="title-item">{title}</Typography>
            </Box>

            {/* Selection tiles */}
            <SelectionSet
                variant="single-select"
                selectedValue={selectedValue ?? undefined}
                onChange={handleChange}
                aria-label={title}
                style={{ gap: 'var(--swan-sem-space-3)' }}
            >
                {options.map((option) => (
                    <SelectionSetInput key={option.value} value={option.value}>
                        <SelectionSetLabel
                            style={{
                                padding: 0,
                                ...(selectedValue !== option.value && {
                                    backgroundColor: 'var(--swan-sem-color-bg-standard)',
                                }),
                            }}
                        >
                            <FlexBox
                                as="span"
                                alignItems="center"
                                gap={3}
                                style={{
                                    padding: 'var(--swan-sem-space-6) var(--swan-sem-space-5)',
                                }}
                            >
                                <Icon iconType={iconTypeMap[option.icon]} size="standard" />
                                <Typography fontSkin="body-standard-bold" as="span">
                                    {option.name}
                                </Typography>
                            </FlexBox>
                        </SelectionSetLabel>
                    </SelectionSetInput>
                ))}
            </SelectionSet>

            {/* Footer: Actions */}
            <FlexBox
                justifyContent="space-between"
                alignItems="center"
                style={{
                    marginTop: 'var(--swan-sem-space-5)',
                }}
            >
                <HelpMeAnswerButton
                    selectorType="icon-tiles"
                    options={options.map(({ name }) => ({ name }))}
                />
                <Button skin="secondary" onClick={handleContinue} disabled={!selectedValue}>
                    Continue
                </Button>
            </FlexBox>
        </Box>
    );
}
